import React, { FC, ChangeEvent, KeyboardEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Input, InputProps, InputAdornment } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import { noop } from 'lodash';
import SearchIcon from '@material-ui/icons/Search';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import classNames from 'classnames';

interface IProps extends WithStyles<typeof styles> {
  query: string;
  focused: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onSearch?: (value: string) => void;
  onClear?: () => void;
  onFocus?: () => void;
  onKeyDown?: (event: KeyboardEvent) => void;
  inputClasses?: InputProps['classes'];
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: 'transparent',
      borderRadius: 0,
      fontWeight: theme.typography.fontWeightBold,
    },
    input: {
      padding: 0,
    },
    icon: {
      fontSize: 28,
      transition: 'color 100ms ease-in-out',
    },
    inactive: {
      color: theme.custom.palette.grey2,
    },
    focused: {
      color: theme.custom.palette.black1,
    },
    deleteIcon: {
      transform: 'rotate(45deg)',
      color: theme.custom.palette.grey3,
    },
    transparent: {
      color: 'transparent',
    },
  });

const Search: FC<IProps> = props => {
  const {
    classes,
    query,
    focused,
    onChange,
    onClear,
    onFocus,
    onKeyDown,
    onSearch,
    inputClasses,
  } = props;
  const { t } = useTranslation();
  const inputEl = useRef(null);

  function handleInputKeyDown(event: KeyboardEvent) {
    const { key } = event;

    if (key === 'Enter' && !!query) {
      return handleSearch();
    }

    onKeyDown(event);
  }

  function handleClear() {
    inputEl.current.focus();
    onClear();
  }

  function handleSearch() {
    inputEl.current.blur();
    onSearch(query);
  }

  function renderStartAdornment() {
    const searchIconClasses = classNames(classes.icon, {
      [classes.inactive]: !focused,
      [classes.focused]: focused,
    });

    return (
      <InputAdornment position="start">
        <SearchIcon className={searchIconClasses} />
      </InputAdornment>
    );
  }

  function renderEndAdornment() {
    return (
      <InputAdornment position="end">
        <IconButton disabled={!query} onClick={handleClear}>
          <AddCircleIcon className={!!query ? classes.deleteIcon : classes.transparent} />
        </IconButton>
      </InputAdornment>
    );
  }

  return (
    <Input
      value={query}
      onFocus={onFocus}
      onChange={onChange}
      onKeyDown={handleInputKeyDown}
      classes={{ root: classes.root, input: classes.input, ...inputClasses }}
      placeholder={t('common:search')}
      inputRef={inputEl}
      startAdornment={renderStartAdornment()}
      endAdornment={renderEndAdornment()}
    />
  );
};

Search.defaultProps = {
  onClear: noop,
  onFocus: noop,
  onKeyDown: noop,
  onSearch: noop,
  inputClasses: {},
};

export default withStyles(styles)(Search);
